import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import RequirementCard from "./components/RequirementCard";
import SubmitRequirementForm from "./components/SubmitRequirementForm";
import SendQuoteModal from "./components/SendQuoteModal";
import NotificationPanel from "./components/NotificationPanel";
import { mockBuyerRequirements, BuyerRequirement } from "@/mocks/buyerRequirements";
import SEO from "@/components/base/SEO";

const CATEGORIES = ["All", "Electronics", "Beauty", "Sports", "Home", "Fashion", "Food & Beverage"];
const COUNTRIES_FILTER = ["All Countries", "United Kingdom", "Canada", "Australia", "Germany", "France", "Japan"];

// Toggle to true to preview factory member view
const IS_FACTORY_MEMBER = false;

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const buyerBoardSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Global Buyer Requirements Board — StarBookPrime",
    "description": "Buyers from 120+ countries post bulk order requirements. Verified factories view contact details and send quotations directly. Free to submit.",
    "url": `${SITE_URL}/buyer-requirements`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Buyer Requirements Board", "item": `${SITE_URL}/buyer-requirements` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Global Buyer Requirements Board",
    "description": "Submit your bulk order requirement for free. Verified factories from USA, China, Europe, and Asia will send competitive quotations to your email. 2,400+ active requirements from 120+ countries.",
    "provider": {
      "@type": "Organization",
      "name": "StarBookPrime",
      "url": SITE_URL,
    },
    "serviceType": "B2B Buyer-Supplier Matching",
    "areaServed": "Worldwide",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free for buyers to submit requirements. Verified factory membership $8/month to access buyer contacts.",
    },
  },
];

const WELCOME_COUNTRIES = [
  { flag: "🇺🇸", name: "USA" },
  { flag: "🇬🇧", name: "UK" },
  { flag: "🇩🇪", name: "Germany" },
  { flag: "🇯🇵", name: "Japan" },
  { flag: "🇦🇺", name: "Australia" },
  { flag: "🇨🇦", name: "Canada" },
  { flag: "🇫🇷", name: "France" },
  { flag: "🇰🇷", name: "Korea" },
  { flag: "🇮🇳", name: "India" },
  { flag: "🇧🇷", name: "Brazil" },
  { flag: "🇦🇪", name: "UAE" },
  { flag: "🇸🇬", name: "Singapore" },
];

export default function BuyerRequirementsPage() {
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeCountry, setActiveCountry] = useState("All Countries");
  const [searchQuery, setSearchQuery] = useState("");
  const [quoteTarget, setQuoteTarget] = useState<BuyerRequirement | null>(null);

  const filtered = mockBuyerRequirements.filter((r) => {
    const matchCat = activeCategory === "All" || r.category === activeCategory;
    const matchCountry = activeCountry === "All Countries" || r.country === activeCountry;
    const matchSearch = !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase()) || r.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchCountry && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Global Buyer Requirements Board — Post Bulk Orders | StarBookPrime"
        description="Submit your bulk order requirements for free. Verified factories from USA, China, Europe, and Asia will send competitive quotations to your email. 2,400+ active requirements from 120+ countries."
        keywords="bulk order requirements, buyer RFQ board, wholesale buyer requirements"
        canonical="/buyer-requirements"
        ogImage="https://public.readdy.ai/ai/img_res/db21d7e6e102fa46db9b8307f50fd2f5.jpg"
        schema={buyerBoardSchema}
      />
      <Navbar />
      <div className="pt-16">

        {/* ===== HERO — Welcome Global Buyers ===== */}
        <div className="relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=global%20international%20trade%20world%20map%20connections%20business%20commerce%20warm%20orange%20amber%20gradient%20abstract%20geometric%20shapes%20professional%20clean%20minimal%20background&width=1440&height=600&seq=buyerhero2&orientation=landscape"
            alt="Global Buyer Requirements Board"
            className="w-full h-[420px] md:h-[520px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                  <i className="ri-global-line" /> Global Buyer Board
                </span>
                <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
                  Buyers from Every Country<br />Welcome Here
                </h1>
                <p className="text-white/80 text-sm md:text-base mb-6 leading-relaxed max-w-xl">
                  Submit your bulk order requirement for <strong className="text-white">free</strong>. Verified USA factories will review your specs and send competitive quotations directly to your email and WhatsApp. Your contact info stays <strong className="text-white">100% private</strong>.
                </p>

                {/* Country flags */}
                <div className="flex flex-wrap gap-2 mb-7">
                  {WELCOME_COUNTRIES.map((c) => (
                    <span key={c.name} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-medium rounded-full">
                      {c.flag} {c.name}
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-medium rounded-full">
                    +100 more
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center justify-center gap-2 px-7 py-3.5 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-circle-line text-base" /> Submit Your Requirement — Free
                  </button>
                  {!IS_FACTORY_MEMBER && (
                    <Link to="/membership" className="flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 backdrop-blur-sm border border-white/30 text-white text-sm font-semibold rounded-full hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-vip-crown-line" /> Factory Membership — Unlock Contacts
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== HOW IT WORKS — Privacy Explained ===== */}
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: "ri-file-add-line", title: "Buyer Submits Free", desc: "Any buyer from any country submits their bulk order requirement with quantity, budget, and specs", color: "bg-orange-50 text-orange-500", step: "1" },
                { icon: "ri-lock-line", title: "Contact Stays Private", desc: "Your email and WhatsApp are completely hidden from the public — never visible to anyone without membership", color: "bg-green-50 text-green-500", step: "2" },
                { icon: "ri-vip-crown-line", title: "Factory Unlocks", desc: "Paid membership verified factories ($8/mo) gain access to your contact details and can send quotations", color: "bg-amber-50 text-amber-500", step: "3" },
                { icon: "ri-shake-hands-line", title: "Deal Closed", desc: "Factory sends quotation to your email. You review and respond directly to close the deal", color: "bg-rose-50 text-rose-500", step: "4" },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${s.color} rounded-xl flex-shrink-0 relative`}>
                    <i className={`${s.icon} text-lg`} />
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full">{s.step}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 mb-0.5">{s.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

          {/* Notification Panel for Buyers */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-bold text-gray-900">Your Requirement Activity</h2>
              <p className="text-xs text-gray-500 mt-0.5">Get notified when factories view or quote your requirements</p>
            </div>
            <NotificationPanel />
          </div>

          {/* Email Alert Info Strip */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
              <i className="ri-mail-check-line text-green-600 text-xl" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-green-800">Email Notifications Active</p>
              <p className="text-xs text-green-700 mt-0.5">
                You&apos;ll receive an <strong>instant email alert</strong> whenever a verified factory views your requirement or sends you a quotation. Your contact info stays private until you respond.
              </p>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-xs font-bold rounded-full flex-shrink-0">
              <i className="ri-checkbox-circle-fill" /> Alerts ON
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[
              { value: "2,400+", label: "Active Requirements", icon: "ri-file-list-3-line", color: "text-orange-500" },
              { value: "120+", label: "Countries", icon: "ri-global-line", color: "text-green-500" },
              { value: "$2.8B+", label: "Total Order Value", icon: "ri-money-dollar-circle-line", color: "text-orange-500" },
              { value: "850+", label: "Verified Factories", icon: "ri-building-line", color: "text-amber-500" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-50 rounded-lg mx-auto mb-2">
                  <i className={`${s.icon} ${s.color} text-lg`} />
                </div>
                <p className="text-xl font-extrabold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Factory Member Upgrade Banner */}
          {!IS_FACTORY_MEMBER && (
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                  <i className="ri-lock-unlock-line text-white text-2xl" />
                </div>
                <div>
                  <p className="text-white font-bold text-base">Factory? Unlock All Buyer Contacts</p>
                  <p className="text-white/80 text-xs mt-0.5">Upgrade to Factory Membership ($8/month) to view buyer email, WhatsApp, and send quotations directly to close deals</p>
                </div>
              </div>
              <Link to="/membership" className="px-6 py-2.5 bg-white text-orange-600 text-sm font-bold rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0">
                Upgrade Now — $8/mo
              </Link>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
            <div className="relative mb-4">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search requirements by product, keyword..."
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeCategory === cat ? "bg-orange-500 text-white" : "bg-gray-50 border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <i className="ri-global-line text-gray-400 text-sm" />
              {COUNTRIES_FILTER.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCountry(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeCountry === c ? "bg-gray-900 text-white" : "bg-gray-50 border border-gray-200 text-gray-600 hover:border-gray-400"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Showing <span className="font-semibold text-gray-700">{filtered.length}</span> requirements
            </p>
          </div>

          {/* Requirements Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-4">
                <i className="ri-file-search-line text-3xl text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No requirements found</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {filtered.map((req) => (
                <RequirementCard
                  key={req.id}
                  req={req}
                  isFactoryMember={IS_FACTORY_MEMBER}
                  onSendQuote={(r) => setQuoteTarget(r)}
                />
              ))}
            </div>
          )}

          {/* ===== BUYER CTA SECTION ===== */}
          <div className="mt-12 rounded-2xl overflow-hidden relative">
            <img
              src="https://readdy.ai/api/search-image?query=diverse%20international%20business%20people%20from%20different%20countries%20meeting%20handshake%20trade%20deal%20warm%20professional%20office%20background%20orange%20amber%20tones&width=1440&height=400&seq=buyercta1&orientation=landscape"
              alt="Global buyers welcome"
              className="w-full h-64 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40 flex items-center">
              <div className="max-w-7xl mx-auto px-8 md:px-12 w-full">
                <div className="max-w-xl">
                  <h3 className="text-2xl font-extrabold text-white mb-2">Are You a Global Buyer?</h3>
                  <p className="text-gray-300 text-sm mb-5 leading-relaxed">
                    Submit your bulk order requirement for free. Verified factories from USA, China, Europe, and Asia will send you competitive quotations directly to your email. Your contact info is always private.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {["🇺🇸 USA", "🇨🇳 China", "🇩🇪 Germany", "🇯🇵 Japan", "🇰🇷 Korea", "🇮🇳 India", "🇮🇹 Italy", "🇹🇷 Turkey"].map((c) => (
                      <span key={c} className="px-3 py-1.5 bg-white/10 text-white text-xs font-medium rounded-full">{c}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 px-7 py-3.5 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-add-circle-line" /> Submit Your Requirement — Free
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Guarantee */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: "ri-lock-line", title: "100% Private Contact", desc: "Your email and WhatsApp are never shown publicly. Only paid verified factories can see them.", color: "bg-green-50 text-green-500" },
              { icon: "ri-shield-check-line", title: "Verified Factories Only", desc: "Only factories with paid membership ($8/mo) and verified status can access your contact details.", color: "bg-orange-50 text-orange-500" },
              { icon: "ri-mail-check-line", title: "Quotations to Your Email", desc: "Factories send quotations directly to your email. You choose who to respond to — no pressure.", color: "bg-amber-50 text-amber-500" },
            ].map((g) => (
              <div key={g.title} className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4">
                <div className={`w-10 h-10 flex items-center justify-center ${g.color} rounded-xl flex-shrink-0`}>
                  <i className={`${g.icon} text-lg`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 mb-1">{g.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{g.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Submit Form Modal */}
      {showForm && (
        <SubmitRequirementForm
          onClose={() => setShowForm(false)}
          onSuccess={() => { setShowForm(false); setShowSuccess(true); }}
        />
      )}

      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border border-green-200 rounded-2xl p-5 flex items-start gap-4 max-w-sm">
          <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-full flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-green-500 text-xl" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900">Requirement Submitted!</p>
            <p className="text-xs text-gray-500 mt-0.5">Verified factories will review your requirement and send quotations to your email. Your contact info is kept private.</p>
          </div>
          <button onClick={() => setShowSuccess(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0">
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Send Quote Modal */}
      {quoteTarget && (
        <SendQuoteModal req={quoteTarget} onClose={() => setQuoteTarget(null)} />
      )}

      <Footer />
    </div>
  );
}
